import { Box } from '@gemini/core';
import { grey200 } from '@mfe-presentation/constants/colors';
import { progressHeightDesktop, progressHeightTablet } from '@mfe-presentation/constants/layout';
import { useAppContext } from '@mfe-presentation/contexts';
import { TLogo } from '@mfe-types/logo';
import { useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { useLocation } from 'react-router-dom';

interface ITopBar {
    logo: TLogo;
}

const TopBar = ({ logo: Logo }: ITopBar) => {
    const { t } = useTranslation(NAME_SPACE);
    const {
        currentStep: { displayedStepNumber, name },
        maxDisplayedStep,
        isFromUpdatePage,
    } = useAppContext();
    const location = useLocation();

    const isStepNumberDisplayed = displayedStepNumber !== undefined && !isFromUpdatePage;

    const progressionWidth = () =>
        isStepNumberDisplayed
            ? `${Math.round((displayedStepNumber / maxDisplayedStep) * 100)}%`
            : '0'; // TODO [WLEF-481] make better flow of progressbar value

    const ariaLabel = process.env.NX_PORTAL_URL?.replace('https://', '').replace('www.', '');

    return (
        <>
            <Box
                position="fixed"
                top="0"
                left="0"
                zIndex="zIndex.1"
                display="flex"
                alignItems="center"
                justifyContent={{
                    'breakpoint.base': 'space-between',
                    'breakpoint.sm': 'flex-start',
                }}
                gap={{
                    'breakpoint.sm': 'spacing.56',
                }}
                height="sizing.48"
                paddingVertical="0"
                paddingHorizontal="spacing.16"
                width="100%"
                backgroundColor="color.background.default"
            >
                <Box
                    as="a"
                    display="inline-flex"
                    aria-label={ariaLabel}
                    href={process.env.NX_PORTAL_URL}
                >
                    <Box
                        testId="svg-logo"
                        as={Logo}
                        style={{
                            cursor: 'pointer',
                        }}
                    />
                </Box>
                {isStepNumberDisplayed ? (
                    <Box
                        as="p"
                        marginLeft="spacing.16"
                        typography="typography.body.14.regular"
                        aria-live="assertive"
                    >
                        {t(`step_${name}.header.stepper`, {
                            replace: {
                                currentStep: displayedStepNumber,
                                maxStep: maxDisplayedStep,
                            },
                        })}
                    </Box>
                ) : null}
            </Box>

            <Box
                position="fixed"
                left="0"
                top="sizing.48"
                zIndex="zIndex.1"
                alignSelf="stretch"
                width="100%"
                height={
                    isStepNumberDisplayed
                        ? {
                              'breakpoint.base': `${progressHeightTablet}px`,
                              'breakpoint.sm': `${progressHeightDesktop}px`,
                          }
                        : {
                              'breakpoint.base': '1px',
                          }
                }
                unsafe_style={{
                    backgroundColor: `${grey200}`,
                }}
            >
                <Box
                    testId="progressBar"
                    height="100%"
                    width={progressionWidth()}
                    backgroundColor="color.surface.brand.primary.default"
                    transitionDuration={
                        location.pathname === '/result' ? undefined : 'motionDuration.300'
                    }
                    transitionTimingFunction="motionEasing.ease-out"
                    transitionProperty="width"
                />
            </Box>
        </>
    );
};

export default TopBar;
